import React from "react";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { useI18n } from "@/i18n";
import { Car, TrainFront } from "lucide-react";

export default function Directions() {
  const { t } = useI18n();
  const d = t.directions;
  const DISTANCES = d.distances;
  return (
    <section
      id="dojazd"
      data-testid="directions-section"
      className="relative bg-canvas-soft py-14 md:py-20 overflow-hidden"
    >
      {/* Subtle red sun motif in background */}
      <div
        className="sun-motif w-[420px] h-[420px] -bottom-32 -left-24 opacity-50 hidden md:block"
        aria-hidden="true"
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="grid grid-cols-12 gap-8 md:gap-16 items-end mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-6">
            <Reveal>
              <p className="overline mb-6">{d.overline}</p>
              <h2 className="serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-ink">
                {d.headingA} <br />
                <span className="italic text-brand-red/80">{d.headingB}</span>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8">
            <Reveal delay={0.15}>
              <p className="text-ink-muted text-base md:text-lg leading-relaxed">
                {d.intro}
              </p>
            </Reveal>
          </div>
        </div>

        {/* Distance cards */}
        <Stagger className="grid grid-cols-1 md:grid-cols-4 gap-px bg-ink/10 border border-ink/10 mb-16">
          {DISTANCES.map((d, i) => (
            <StaggerItem
              key={d.city}
              className="bg-canvas p-8 md:p-10 hover:bg-canvas-surface transition-colors duration-500"
            >
              <div className="flex items-baseline gap-3 mb-6">
                <span className="text-brand-red text-xs tracking-[0.3em]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="h-px flex-1 bg-ink/20" />
              </div>
              <h3 className="serif text-3xl md:text-4xl font-light text-ink leading-tight mb-4">
                {d.city}
              </h3>
              <p className="serif italic text-xl text-brand-red mb-2">{d.km}</p>
              <p className="text-ink-muted text-sm leading-relaxed">{d.time}</p>
            </StaggerItem>
          ))}
        </Stagger>

        {/* Travel modes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          <Reveal>
            <div className="flex items-start gap-5">
              <Car size={28} strokeWidth={1} className="text-brand-red flex-shrink-0 mt-1" />
              <div>
                <p className="overline mb-2">{d.carKicker}</p>
                <h4 className="serif text-2xl text-ink font-light mb-3">
                  {d.carTitle}
                </h4>
                <p className="text-ink-muted text-sm leading-relaxed">
                  {d.carDesc}
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="flex items-start gap-5">
              <TrainFront size={28} strokeWidth={1} className="text-brand-red flex-shrink-0 mt-1" />
              <div>
                <p className="overline mb-2">{d.trainKicker}</p>
                <h4 className="serif text-2xl text-ink font-light mb-3">
                  {d.trainTitle}
                </h4>
                <p className="text-ink-muted text-sm leading-relaxed">
                  {d.trainDescA}{" "}
                  <span className="text-ink">{d.trainStations[0]}</span>,{" "}
                  <span className="text-ink">{d.trainStations[1]}</span>,{" "}
                  <span className="text-ink">{d.trainStations[2]}</span>. {d.trainDescB}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
